import { PaymentMethodType, PaymentMethods } from 'Jsx/CheckoutV4/types';

import {
  GetPaymentMethodQuery_paymentMethod as PaymentMethodsQuery,
  GetPaymentMethodQuery_paymentMethod_cardMethods as CardMethods,
  GetPaymentMethodQuery_paymentMethod_extraMethods as ExtraMethods,
  GetPaymentMethodQuery_paymentMethod_offlineMethods as OfflineMethods,
} from 'Jsx/CheckoutV4/Graphql/Query/types/GetPaymentMethodQuery';

const formatCardPaymentMethod = ({
  methods,
}: {
  methods: (CardMethods | null)[];
}): PaymentMethodType[] => {
  const paymentMethods: PaymentMethodType[] = [];

  methods.forEach(method => {
    if (method?.paymentGatewayId && method?.title) {
      paymentMethods.push({
        paymentGatewayId: method.paymentGatewayId,
        title: method?.title,
        text: method?.text || undefined,
        logoUrl: method?.logoUrl || undefined,
        extraCommission: method?.extraCommission || undefined,
        maxAmount: method?.maxAmount || undefined,
        isMethodWithText: Boolean(method?.text),
        isSelected: false,
      });
    }
  });

  return paymentMethods;
};

const formatOfflinePaymentMethod = ({
  methods,
}: {
  methods: (OfflineMethods | null)[];
}): PaymentMethods => {
  const paymentMethods: PaymentMethods = {};

  methods.forEach(method => {
    if (method?.paymentGatewayId && method?.title) {
      paymentMethods[method.title] = {
        paymentGatewayId: method.paymentGatewayId,
        title: method?.title,
        text: method?.text || undefined,
        logoUrl: method?.logoUrl || undefined,
        extraCommission: method?.extraCommission || undefined,
        maxAmount: method?.maxAmount || undefined,
        logoMiniatureUrl: method?.logoMiniatureUrl || undefined,
        offlineLabel: method?.offlineLabel || undefined,
        slug: method?.slug || undefined,
        isMethodWithText: Boolean(method?.text),
        isSelected: false,
      };
    }
  });

  return paymentMethods;
};
const formatExtraPaymentMethod = ({
  methods,
}: {
  methods: (CardMethods | ExtraMethods | OfflineMethods | null)[];
}): PaymentMethods => {
  const paymentMethods: PaymentMethods = {};

  methods.forEach(method => {
    if (method?.paymentGatewayId && method?.title) {
      paymentMethods[method.title] = {
        paymentGatewayId: method.paymentGatewayId,
        title: method?.title,
        text: method?.text || undefined,
        logoUrl: method?.logoUrl || undefined,
        isMethodWithText: Boolean(method?.text),
        isSelected: false,
      };
    }
  });

  return paymentMethods;
};

const formatPaymentMethods = (
  paymentMethodsQuery: PaymentMethodsQuery,
): {
  selectedOnlinePaymentMethod?: PaymentMethodType;
  selectedOfflinePaymentMethod?: PaymentMethodType;
  paymentMethods: PaymentMethods;
  offlinePaymentMethods: PaymentMethods;
  onlinePaymentMethods: PaymentMethodType[];
} => {
  const { cardMethods, extraMethods, offlineMethods } = paymentMethodsQuery;

  // debit and credit card
  const onlinePaymentMethods = formatCardPaymentMethod({
    methods: cardMethods || [],
  });

  let selectedOnlinePaymentMethod = onlinePaymentMethods[0];

  // extra methods: paypal, rappi, pse, etc
  const extraPaymentMethods = formatExtraPaymentMethod({
    methods: extraMethods || [],
  });

  // offline payments
  const offlinePaymentMethods = formatOfflinePaymentMethod({
    methods: offlineMethods || [],
  });
  const defaultOfflinePaymentMethodKey = offlineMethods?.[0]?.title || '';
  const selectedOfflinePaymentMethod =
    offlinePaymentMethods[defaultOfflinePaymentMethodKey];

  const paymentMethods = {
    [selectedOnlinePaymentMethod?.title as string]: selectedOnlinePaymentMethod,
    ...extraPaymentMethods,
    ...offlinePaymentMethods,
  };

  if (!selectedOnlinePaymentMethod && extraMethods?.length) {
    selectedOnlinePaymentMethod =
      extraPaymentMethods[extraMethods[0]?.title || ''];
  }

  return {
    selectedOnlinePaymentMethod,
    selectedOfflinePaymentMethod,
    paymentMethods,
    offlinePaymentMethods,
    onlinePaymentMethods,
  };
};

export default formatPaymentMethods;
